import React, { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  SearchBar,
  SearchSuggestion,
} from "../components/conversation/SearchBar";
import { ConversationList } from "../components/conversation/ConversationList";
import { ConversationViewer } from "../components/conversation/ConversationViewer";
import { SearchFilters, DateRange } from "../types/conversation";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Card } from "../components/ui/card";
import { AlertCircle } from "lucide-react";

const ChatHistoryPage: React.FC = () => {
  const { isConfigured } = useAuth();
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [searchSuggestions] = useState<SearchSuggestion[]>([
    { id: "1", text: "sales data", type: "popular" },
    { id: "2", text: "customer analysis", type: "popular" },
    { id: "3", text: "revenue reports", type: "popular" },
    { id: "4", text: "inventory management", type: "recent" },
    { id: "5", text: "financial dashboard", type: "recent" },
  ]);

  // Handle search query changes
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle date range filter changes
  const handleDateRangeChange = useCallback((range: string) => {
    let dateRange: DateRange | undefined;
    const now = new Date();

    switch (range) {
      case "today":
        dateRange = {
          start: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
          end: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            23,
            59,
            59
          ),
        };
        break;
      case "week":
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateRange = {
          start: weekAgo,
          end: now,
        };
        break;
      case "month":
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        dateRange = {
          start: monthAgo,
          end: now,
        };
        break;
      case "quarter":
        const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        dateRange = {
          start: quarterAgo,
          end: now,
        };
        break;
      case "year":
        const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        dateRange = {
          start: yearAgo,
          end: now,
        };
        break;
      default:
        dateRange = undefined;
    }

    setSearchFilters((prev) => ({
      ...prev,
      dateRange,
    }));
  }, []);

  // Handle conversation selection
  const handleSelectConversation = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
  }, []);

  // Handle conversation editing
  const handleEditConversation = useCallback(
    (conversationId: string, newTitle: string) => {
      // TODO: Implement conversation editing logic
      console.log("Edit conversation:", conversationId, newTitle);
    },
    []
  );

  // Handle conversation deletion
  const handleDeleteConversation = useCallback(
    (conversationId: string) => {
      // TODO: Implement conversation deletion logic
      console.log("Delete conversation:", conversationId);
      // If the deleted conversation was selected, clear the selection
      if (selectedConversationId === conversationId) {
        setSelectedConversationId(null);
      }
    },
    [selectedConversationId]
  );

  // Handle search suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: SearchSuggestion) => {
    setSearchQuery(suggestion.text);
  }, []);

  // Handle follow-up clicks from conversation viewer
  const handleFollowUpClick = useCallback((question: string) => {
    // TODO: Navigate to query page with the follow-up question
    console.log("Follow-up question:", question);
  }, []);

  if (!isConfigured) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please configure your subdomain in Settings to access chat history.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Chat History</h1>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <SearchBar
          onSearch={handleSearch}
          onDateRangeChange={handleDateRangeChange}
          suggestions={searchSuggestions}
          onSuggestionSelect={handleSuggestionSelect}
          placeholder="Search conversations..."
        />
      </div>

      {/* Three-panel layout */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Left Panel - Conversation List */}
        <div className="w-1/3 min-w-0">
          <Card className="h-full flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Conversations</h2>
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto p-4">
                <ConversationList
                  searchQuery={searchQuery}
                  filters={searchFilters}
                  onSelectConversation={handleSelectConversation}
                  onEditConversation={handleEditConversation}
                  onDeleteConversation={handleDeleteConversation}
                  selectedConversationId={selectedConversationId || undefined}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Right Panel - Conversation Viewer */}
        <div className="flex-1 min-w-0">
          <Card className="h-full flex flex-col">
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto p-6">
                {selectedConversationId ? (
                  <ConversationViewer
                    conversationId={selectedConversationId}
                    onFollowUpClick={handleFollowUpClick}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-muted-foreground">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                        <AlertCircle className="h-8 w-8" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">
                        No conversation selected
                      </h3>
                      <p className="text-sm">
                        Select a conversation from the list to view its details
                        and messages.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPage;
